/**
 * useFloatingPrompt - Shows context-aware prompt suggestions
 *
 * Displays a floating prompt assistant after 1+ AI responses with a 2-second delay.
 * Only appears once per session to avoid annoying users.
 *
 * @param {Object} params
 * @param {Array} params.messages - All messages in current conversation
 * @param {boolean} params.isStreaming - Whether messages are currently streaming
 *
 * @returns {Object}
 * @returns {boolean} isVisible - Whether prompt is currently visible
 * @returns {boolean} hasAppeared - Whether prompt has appeared at least once
 * @returns {Function} handleClose - Close and permanently dismiss the prompt
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const APPEARANCE_DELAY_MS = 2000;

export const useFloatingPrompt = ({ messages, isStreaming }) => {
  const [state, setState] = useState({
    isVisible: false,
    hasAppeared: false
  });

  const timerRef = useRef(null);

  // Main visibility logic
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Don't show if already appeared or currently dismissed
    if (state.hasAppeared) return;

    // Count AI responses (non-user messages)
    const aiResponses = messages.filter(msg => msg.sender !== 'user');

    // Need at least 1 AI response and must not be streaming
    if (aiResponses.length >= 1 && !isStreaming) {
      timerRef.current = setTimeout(() => {
        setState({
          isVisible: true,
          hasAppeared: true
        });
      }, APPEARANCE_DELAY_MS);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [messages, isStreaming, state.hasAppeared]);

  // Close handler
  const handleClose = useCallback(() => {
    setState(prev => ({
      ...prev,
      isVisible: false
    }));
  }, []);

  return {
    isVisible: state.isVisible,
    hasAppeared: state.hasAppeared,
    handleClose
  };
};
